import type { ChatMentionsMenuItemType } from '../chat/mentions/types';
import { MentionEntityMenuIcon } from '../chat/mentions/MentionEntityMenuIcon';
import type { FindingMentionData } from '@terra/shared-dto-chat-types';
import type { InternalEnrichedFinding } from '../../types';
import type { PocBlocks } from '@terra/shared-schemas-orchestrator';
import { createElement } from 'react';
import { formatCategoryForDisplay } from '../../utils/category-utils';
import { getFindingCvssDisplayValues } from '@terra/shared-cvss';

const MAX_POC_HTTP_TEXT_LENGTH = 2000;

function capHttpText(text: string, harId?: string | null): string {
  if (text.length <= MAX_POC_HTTP_TEXT_LENGTH) {
    return text;
  }

  const harHint = harId ? ` — fetch HAR ${harId} for the full text` : '';

  return `${text.slice(0, MAX_POC_HTTP_TEXT_LENGTH)}\n… [truncated ${text.length - MAX_POC_HTTP_TEXT_LENGTH} chars${harHint}]`;
}

function serializePocBlocks(pocBlocks: PocBlocks): string {
  return pocBlocks
    .map((block) => {
      if (block.type === 'narrative') {
        return block.content;
      }

      if (block.type === 'image') {
        return block.caption?.trim()
          ? `[Image] ${block.caption}`
          : `[Image] ${block.fileName}`;
      }

      const parts = [block.explanation];

      if (block.whyItMatters)
        parts.push(`Why it matters: ${block.whyItMatters}`);
      if (block.manualRequest)
        parts.push(
          `Request:\n${capHttpText(block.manualRequest, block.toolExecutionHarId)}`,
        );
      if (block.manualResponse)
        parts.push(
          `Response:\n${capHttpText(block.manualResponse, block.toolExecutionHarId)}`,
        );

      return parts.join('\n');
    })
    .join('\n\n');
}

function getPocText(
  poc: string | null | undefined,
  pocBlocks: PocBlocks | null | undefined,
): string | null {
  if (poc) return poc;
  if (pocBlocks && pocBlocks.length > 0) return serializePocBlocks(pocBlocks);

  return null;
}

function affectedAssetLabel(asset: InternalEnrichedFinding['assets'][number]) {
  if (asset.method && asset.path) {
    return `${asset.method} ${asset.path}`;
  }

  if (asset.url) {
    return asset.url;
  }

  if (asset.path) {
    return asset.path;
  }

  if (asset.type === 'ENDPOINT') {
    return undefined;
  }

  return asset.id;
}

/**
 * Format a finding as a raw text string for the AI backend
 */
function formatFindingRawText(finding: InternalEnrichedFinding): string {
  const parts: string[] = [];

  parts.push(`# Finding: ${finding.title}`);
  parts.push(`Finding ID: ${finding.id}`);
  parts.push(`Severity: ${finding.severity}`);
  parts.push(`Status: ${finding.status}`);

  if (finding.category) {
    parts.push(`Category: ${formatCategoryForDisplay(finding.category)}`);
  }

  const cvssDisplay = getFindingCvssDisplayValues(finding.cvss);

  if (cvssDisplay.score) {
    parts.push(`CVSS Score: ${cvssDisplay.score}`);
    if (cvssDisplay.vector) {
      parts.push(`CVSS Vector: ${cvssDisplay.vector}`);
    }
  }

  if (finding.shortDescription) {
    parts.push(`\nShort Description: ${finding.shortDescription}`);
  }

  if (finding.description) {
    parts.push(`\nDescription:\n${finding.description}`);
  }

  const pocText = getPocText(finding.poc, finding.pocBlocks);

  if (pocText) {
    parts.push(`\nProof of Concept:\n${pocText}`);
  }

  if (finding.businessImpact) {
    parts.push(`\nBusiness Impact:\n${finding.businessImpact}`);
  }

  if (finding.mitigationPlan) {
    parts.push(`\nMitigation Plan:\n${finding.mitigationPlan}`);
  }

  if (finding.assets && finding.assets.length > 0) {
    const assetStrings = finding.assets.flatMap((asset) => {
      const label = affectedAssetLabel(asset);

      return label ? [label] : [];
    });

    if (assetStrings.length > 0) {
      parts.push(`\nAffected Assets:\n${assetStrings.join('\n')}`);
    }
  }

  const endpointId = finding.assets?.find(
    (asset) => asset.type === 'ENDPOINT',
  )?.id;

  if (endpointId) {
    parts.push(`\nEndpoint ID: ${endpointId}`);
  }

  if (finding.signalId) {
    parts.push(`\nSignal ID: ${finding.signalId}`);
  }

  return parts.join('\n');
}

/**
 * Create a finding mention item for use in chat
 */
function findingMentionLabel(finding: InternalEnrichedFinding): string {
  const trimmedTitle = finding.title.trim();

  if (trimmedTitle.length > 50) {
    return `${trimmedTitle.slice(0, 47)}...`;
  }

  if (trimmedTitle) {
    return trimmedTitle;
  }

  return finding.id ? `Finding ${finding.id.slice(-4)}` : 'Finding';
}

export function createFindingMention(
  finding: InternalEnrichedFinding,
): ChatMentionsMenuItemType {
  const label = findingMentionLabel(finding);
  const rawText = formatFindingRawText({
    ...finding,
    title: finding.title.trim() || label,
  });

  const affectedAssets = finding.assets
    ?.flatMap((asset) => {
      const label = affectedAssetLabel(asset);

      return label ? [label] : [];
    })
    .filter(Boolean);

  const cvssDisplay = getFindingCvssDisplayValues(finding.cvss);

  const findingData: FindingMentionData = {
    title: finding.title,
    severity: finding.severity,
    category: finding.category || undefined,
    status: finding.status,
    shortDescription: finding.shortDescription || undefined,
    cvssScore: cvssDisplay.score ?? undefined,
    cvssVector: cvssDisplay.vector ?? undefined,
    description: finding.description || undefined,
    poc: getPocText(finding.poc, finding.pocBlocks) || undefined,
    businessImpact: finding.businessImpact || undefined,
    mitigationPlan: finding.mitigationPlan || undefined,
    affectedAssets: affectedAssets?.length ? affectedAssets : undefined,
    signalId: finding.signalId || undefined,
    rawText,
  };

  return {
    id: `finding-${finding.id}`,
    label,
    icon: createElement(MentionEntityMenuIcon, { entityType: 'Finding' }),
    type: 'child',
    mentionEntity: {
      entityType: 'Finding',
      entityId: finding.id,
      label,
      extraData: { finding: findingData },
    },
  };
}
